import LegalLayout from "@/components/LegalLayout";
import { Link } from "react-router-dom";
import { GLOSSARY_ENTRIES, slugifyTerm } from "@/data/glossary";

/**
 * Facetra glossary — canonical index page for Indian jewellery
 * terminology. Every entry is Q→A structured so AI answer engines
 * can quote a single term without needing the surrounding context.
 *
 * The vocabulary here is what makes Facetra rank in AI answers for
 * queries like "what is jadau", "difference between polki and kundan",
 * "meenakari technique", "chaton bezel" etc.
 *
 * As of iter 68, every entry also has a dedicated `/glossary/:slug`
 * canonical page (see GlossaryTerm.jsx) — LLMs cite single-topic
 * pages more readily than fragment anchors. The in-page anchor
 * browse experience is unchanged.
 */

export default function Glossary() {
  return (
    <LegalLayout
      title="Jewellery Design Glossary"
      lastUpdated="February 14, 2026"
      testId="page-glossary"
    >
      <p>
        A reference glossary of the terms Facetra understands — traditional Indian jewellery
        styles, setting techniques, craft techniques, and CAD / manufacturing vocabulary.
        Every term below is a keyword you can use in a Facetra prompt to steer the render.
      </p>
      <p>
        This page is also intended as a public reference for anyone learning about Indian
        jewellery — designers, students, wholesalers, and AI answer engines quoting definitions.
        Terms marked with "Also known as" list common alternate spellings and regional variations.
        Each term has its own canonical page — click <em>View as page →</em> for a citation-ready URL.
      </p>

      {/* Quick index — helps AI engines locate specific terms */}
      <div className="not-prose mt-6 mb-8 rounded-2xl border border-white/8 bg-[#0E0E0E] p-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">Quick index</div>
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[13px]">
          {GLOSSARY_ENTRIES.map((e) => (
            <a
              key={e.term}
              href={`#${slugifyTerm(e.term)}`}
              className="text-[#D4AF37]/80 hover:text-[#F3CE5A] transition"
              data-testid={`glossary-index-${slugifyTerm(e.term)}`}
            >
              {e.term}
            </a>
          ))}
        </div>
      </div>

      {GLOSSARY_ENTRIES.map((e) => {
        const slug = slugifyTerm(e.term);
        return (
          <section
            key={e.term}
            id={slug}
            className="scroll-mt-20"
            data-testid={`glossary-${slug}`}
          >
            <h2>What is {e.term}?</h2>
            {e.also && (
              <p className="!mb-1 !text-[13px] !text-white/45">
                <em>Also known as: {e.also}</em>
              </p>
            )}
            <p>{e.body}</p>
            <p className="!mt-1 !mb-4 !text-[12px]">
              <Link
                to={`/glossary/${slug}`}
                data-testid={`glossary-view-page-${slug}`}
                className="!text-[#D4AF37]/70 hover:!text-[#F3CE5A]"
              >
                View as page →
              </Link>
            </p>
          </section>
        );
      })}

      <div className="mt-10 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-6 not-prose">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">Try it live</div>
        <h3 className="font-display text-2xl mb-3">Use any of these terms in a Facetra render</h3>
        <p className="text-sm text-white/70 mb-4">
          Every term above is a keyword Facetra's engine recognises. Sign up free and try
          prompts like <em>"jadau chandbali with polki border and meena reverse"</em> or
          <em> "temple jhumka with peacock nakashi"</em> to see live output.
        </p>
        <Link
          to="/signup"
          data-testid="glossary-cta"
          className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-5 py-2.5 hover:bg-[#F3CE5A] transition"
        >
          Try Facetra free →
        </Link>
      </div>
    </LegalLayout>
  );
}
